import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import clsx from 'clsx';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Toast, { ToastType } from '../atoms/Toast';
import { SCORE_TYPE } from '../atoms/LabledScore';
import PlayerTrackAndFieldRating from '../organisms/PlayerTrackAndFieldRating';
import PlayerInGameRating from '../organisms/PlayerInGameRating';
import PlayerCombineRating from '../organisms/PlayerCombineRating';
import UpdatedPlayerProfileStats, { TABS_VALUES_ENUM } from '../organisms/UpdatedPlayerProfileStats';
import PlayerProfileTopPlayers from '../organisms/PlayerProfileTopPlayers';
import PageContainer from './PageContainer';
import gql from '../services/gql';
import { updatePageTitle } from '../services/page-title';
import { getPrimaryHighSchool, sortRowsByKey } from '../services/player';
import * as clientStorage from '../services/client-storage';
import { StorageKey } from '../services/client-storage';
import { getQueryParameter } from '../services/query-parameters';
import { State } from '../redux/reducers';
import { UserState } from '../redux/reducers/user';
import { setSelectedCollegeTeam, setSelectedNFLTeam } from '../redux/dispatchers/ui';
import { fetchDivITeams, fetchNFLTeams } from '../redux/dispatchers/teams';
import { setNFLAccessAction } from '../redux/actions/ui';
import { COLOR_WHITE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import MEDIA from '../styles/media';
import User from '../types/User';
import Player, { PlayerCommonData } from '../types/Player';
import Team from '../types/Team';
import Position from '../types/Position';
import HighSchool from '../types/HighSchool';
import College from '../types/College';
import NFLTeam from '../types/NFLTeam';
import { AccessLevelType } from '../types/AccessLevel';
import UpdatedPlayerProfileHeader from '../organisms/UpdatedPlayerProfileHeader';
import PlayerProfileStickyHeader from '../organisms/PlayerProfileStickyHeader';
import UpdatedPlayerProfileHighSchoolOverview from '../organisms/UpdatedPlayerProfileHighSchoolOverview';
import HighSchoolBiometrics from '../organisms/HighSchoolBiometrics';
import On3NilValuation from '../organisms/On3NilValuation';
import HighSchoolProductionTable from '../organisms/HighSchoolProductionTable';
import PlayerValuations from '../organisms/PlayerValuations';
import UpdatedPlayerProfileCollegeOverview from '../organisms/UpdatedPlayerProfileCollegeOverview';
import CollegeCareerOverview from '../molecules/CollegeCareerOverview';
import UpdatedPlayerProfileNFLOverview from '../organisms/UpdatedPlayerProfileNFLOverview';
import CollegeNFLProductionTable from '../organisms/CollegeNFLProductionTable';
import JucoProductionTable from '../organisms/JucoProductionTable';
import NAIAProductionTable from '../organisms/NAIAProductionTable';
import { HighSchoolProductionResponse } from '../types/HighSchoolProductionData';
import { CollegeProductionResponse, PlayerCollegeDataResponse } from '../types/CollegeProductionData';
import PFFPlayerInGameRating from '../organisms/PFFPlayerInGameRating';
import PlayerProfileNotes from '../organisms/PlayerProfileNotes';
import SpecialTeamYearlyPuntKickoffStatsTable from '../organisms/SpecialTeamYearlyPuntKickoffStatsTable';
import SpecialTeamsPlayerParticipationTable from '../organisms/SpecialTeamsPlayerParticipationTable';
import { PlayerValuationProfileFactors } from '../types/PlayerValuationProfileFactors';
import { useQuery } from '@tanstack/react-query';
import { PlayerEvaluationDataResponse } from '../types/Evaluation';
import { PlayerOffersResponse } from '../types/Offer';
import { PlayerCareerDataResponse } from '../types/PlayerCareer';
import { CacheKey, retrieve } from '../services/cache';
import UpdatedPlayerProCombineRating from '../organisms/UpdatedPlayerProCombineRating';

const STARTER_THRESHOLD = 2; // Example threshold for determining if a player is a starter based on college production data
interface PlayerPageTemplateProps {
  user?: User;
  nflAccess?: boolean;
  adminAccess?: boolean;
  printed?: boolean;
  divITeams: Team[];
  divITeamsLoading: boolean;
  nflTeams: Team[];
  nflTeamsLoading: boolean;
  selectedCollegeTeam: Team | undefined;
  selectedNFLTeam: Team | undefined;
  setSelectedCollegeTeam: (selectedCollegeTeam:Team | undefined) => void;
  setSelectedNFLTeam: (selectedNFLTeam:Team | undefined) => void;
  fetchDivITeams: () => void;
  fetchNFLTeams: () => void;
  setNFLAccessAction: (isNFL:boolean) => void;
}

export const TEAM_TYPE = {
  HIGH_SCHOOL: 'High School',
  COLLEGE: 'College',
  NFL: 'NFL',
};

export enum PlayerPDFQueryParameter {
  PROFILE_TYPE = 'profile-type',
  SELECTED_COLLEGE_TEAM_ID = 'selected-college-team-id',
  SELECTED_NFL_TEAM_ID = 'selected-nfl-team-id',
  SELECTED_POSITION = 'selected-position',
  SELECTED_TEAM_TYPE = 'selected-team-type',
}

const useStyles = makeStyles(theme => ({
  playerPageTemplate: {
    fontFamily: FONT_PROXIMA_NOVA,
  },
  printed: {
    display: 'none',
  },

  paper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  detailsWrap: {
    display: 'flex',
    gap: theme.spacing(5),
    margin: theme.spacing(0, 5, 5, 5),
    flexDirection: 'column',

    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(3),
      margin: theme.spacing(0, 3, 3, 3),
    },
    
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
      margin: theme.spacing(0, 2, 2, 2),
    },
  },
  contentScroll: {
    // no-op for now; window handles scrolling
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    background: COLOR_WHITE,
    display: 'flex',
    gap: theme.spacing(3),
    padding: theme.spacing(2, 5, 2, 5),
    flexDirection: 'column',

    '& .MuiTabs-flexContainer': {
      margin: theme.spacing(0, 0, 0, 3),
      gap: theme.spacing(5),
    },

    '& .MuiTabs-flexContainer button:last-child': {
      marginRight: theme.spacing(3),
    },

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
    },

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),

      '& .MuiTabs-flexContainer': {
        gap: theme.spacing(2.5),
      },
    },

    [theme.breakpoints.down('xs')]: {
      position: 'relative',
    },
  },
  selectedTab: {
    fontWeight: 400
  },
  gridContainer: {
  },
  gridContainerColumn: {
    '&:first-child': {
      paddingRight: theme.spacing(2),
    },
    
    '&:last-child': {
      paddingLeft: theme.spacing(3),
    },

    [theme.breakpoints.down('md')]: {
      '&:first-child': {
        paddingRight: theme.spacing(1.5),
      },
      
      '&:last-child': {
        paddingLeft: theme.spacing(1.5),
      },
    },
    
    [theme.breakpoints.down('sm')]: {
      '&:first-child': {
        paddingRight: 0,
      },
      
      '&:last-child': {
        marginTop: theme.spacing(2),
        paddingLeft: 0,
      },
    },
  },
  box: {
    marginBottom: theme.spacing(5),

    '&:empty': {
      marginBottom: 0,
    },

    '&:first-of-type': {
      marginTop: 0,
    },

    '&:last-of-type': {
      marginBottom: 0,
    },

    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  notesContainer: {
  },
  topPlayers: {
  },
}), { name: UpdatedPlayerPageTemplate.name });

const SPECIAL_POSITIONS = ['P', 'K', 'LS'];

export function UpdatedPlayerPageTemplate(props:PlayerPageTemplateProps) {
  const {
    user,
    nflAccess = false,
    adminAccess = false,
    printed = false,
    divITeams,
    divITeamsLoading,
    nflTeams,
    nflTeamsLoading,
    selectedCollegeTeam,
    selectedNFLTeam,
    setSelectedCollegeTeam,
    setSelectedNFLTeam,
    fetchDivITeams,
    fetchNFLTeams,
    setNFLAccessAction,
  } = props;
  const classes = useStyles();
  const { slug } = useParams<any>();
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(true);

  const [player, setPlayer] = useState<Player>();
  const [playerCopy, setPlayerCopy] = useState<Player>();
  const [positions, setPositions] = useState<Position[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<Position>();
  const [selectedTeamType, setSelectedTeamType] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState<string | null>('overview');
  const [requestedProfileStatsTab, setRequestedProfileStatsTab] = useState<{ value: string; key: number } | null>(null);
  const [nilValuation, setNILValuation] = useState<any>(null);
  const [collegeProductionData, setCollegeProductionData] = React.useState<CollegeProductionResponse | null>(null);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);

  const [toastVisible, setToastVisible] = useState<boolean>(false);
  const [toastType, setToastType] = useState<ToastType>(ToastType.SUCCESS);
  const [toastMessage, setToastMessage] = useState<string>('');

  const userProfileTypeFromQueryParameters:string = getQueryParameter(PlayerPDFQueryParameter.PROFILE_TYPE); // nfl | college
  const selectedCollegeTeamIdFromQueryParameters:number = Number(getQueryParameter(PlayerPDFQueryParameter.SELECTED_COLLEGE_TEAM_ID) || 0);
  const selectedNFLTeamIdFromQueryParameters:number = Number(getQueryParameter(PlayerPDFQueryParameter.SELECTED_NFL_TEAM_ID) || 0);
  const selectedPositionFromQueryParameters:string = getQueryParameter(PlayerPDFQueryParameter.SELECTED_POSITION).toUpperCase();
  const selectedTeamTypeFromQueryParameters:string = getQueryParameter(PlayerPDFQueryParameter.SELECTED_TEAM_TYPE); // high-school, college, nfl

  const playerIsJUCO = player && player.playerColleges && player.playerColleges.length > 0 && player?.playerColleges.find((college:College) => college.isPrimary)?.team?.conference?.mainLevel === 'JUCO';
  const playerIsNAIA = player && player.playerColleges && player.playerColleges.length > 0 && player?.playerColleges.find((college:College) => college.isPrimary)?.team?.conference?.mainLevel === 'NAIA';
  const starterYear = collegeProductionData?.collegeProductionData?.map(row => row.gamesStarted).filter(gamesStarted => gamesStarted >= STARTER_THRESHOLD).length || 0;

  function scrollToSection(sectionId: string | null) {
    if (!sectionId) {
      return;
    }
    const element = document.getElementById(sectionId);
    if (!element) {
      return;
    }

    const stickyHeaderHeight = stickyHeaderRef.current?.getBoundingClientRect().height || 0;
    const top = window.pageYOffset + element.getBoundingClientRect().top - stickyHeaderHeight - 12;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: 'smooth',
    });
  }

  useEffect(() => {
    scrollToSection(selectedTab);
    if (selectedTab === 'ppi') {
      setRequestedProfileStatsTab({ value: selectedTab, key: Date.now() });
    }
  }, [selectedTab]);

  function onHeaderScoreNavigate(scoreType: string) {
    if (scoreType === SCORE_TYPE.MPH) {
      if (selectedTeamType === TEAM_TYPE.HIGH_SCHOOL) {
        scrollToSection('hsInGame');
      } else {
        scrollToSection('collegeInGame');
      }
      return;
    }

    const tabValueByScoreType: Record<string, string> = {
      [SCORE_TYPE.PAI]: TABS_VALUES_ENUM.PAI,
      [SCORE_TYPE.COMBINE]: TABS_VALUES_ENUM.COMBINE,
      [SCORE_TYPE.PPI]: TABS_VALUES_ENUM.PPI,
      [SCORE_TYPE.PRO_COMBINE]: TABS_VALUES_ENUM.PRO_COMBINE,
    };

    const tabValue = tabValueByScoreType[scoreType];
    if (!tabValue) {
      return;
    }

    setRequestedProfileStatsTab({ value: tabValue, key: Date.now() });
    setSelectedTab('ppi');
    scrollToSection('ppi');
  }

  useEffect(() => {
    fetchPlayer(slug);
    fetchDivITeams();
  }, [slug]);

  useEffect(() => {
    if (nflAccess && !nflTeams.length) {
      fetchNFLTeams();
    }
  }, [nflAccess]);

  useEffect(() => {
    if (player) {
      updatePositions(player);
    }
  }, [player, selectedTeamType, selectedPositionFromQueryParameters]);

  useEffect(() => {
    if (printed && user?.id && userProfileTypeFromQueryParameters) {
      if (userProfileTypeFromQueryParameters === 'nfl') {
        setNFLAccessAction(true);
      } else if (userProfileTypeFromQueryParameters === 'college') {
        setNFLAccessAction(false);
      }
    }
  }, [printed, user?.id, userProfileTypeFromQueryParameters]);

  useEffect(() => {
    if (printed && selectedCollegeTeamIdFromQueryParameters && divITeams?.length) {
      setSelectedCollegeTeam(divITeams
        .find((team:Team) => team.id === selectedCollegeTeamIdFromQueryParameters)
      );
    } else if (user && Object.keys(user).length > 0 && divITeams?.length) {
      let defaultCollegeTeamId = user?.collegeTeam?.id;

      if (selectedCollegeTeam?.id) {
        defaultCollegeTeamId = selectedCollegeTeam.id;
      } else {
        const selectedCollegeTeamFromClientStorage = clientStorage.get(StorageKey.SELECTED_COLLEGE_TEAM);

        if (selectedCollegeTeamFromClientStorage?.id) {
          defaultCollegeTeamId = selectedCollegeTeamFromClientStorage.id;
        }
      }

      const defaultCollegeTeam = defaultCollegeTeamId
        ? divITeams.find((team:Team) => team.id === defaultCollegeTeamId)
        : divITeams[0];

      setSelectedCollegeTeam(defaultCollegeTeam);
    }
  }, [
    user,
    divITeams?.length,
    selectedCollegeTeam,
    printed,
    selectedCollegeTeamIdFromQueryParameters,
  ]);

  useEffect(() => {
    if (printed && selectedNFLTeamIdFromQueryParameters && nflTeams?.length) {
      setSelectedNFLTeam(nflTeams.find((team:Team) => team.id === selectedNFLTeamIdFromQueryParameters));
    } else if (user && Object.keys(user).length > 0 && nflTeams?.length) {
      let defaultNFLTeamId = user?.nflTeam?.id;

      if (selectedNFLTeam?.id) {
        defaultNFLTeamId = selectedNFLTeam.id;
      } else {
        const selectedNFLTeamFromClientStorage = clientStorage.get(StorageKey.SELECTED_NFL_TEAM);

        if (selectedNFLTeamFromClientStorage?.id) {
          defaultNFLTeamId = selectedNFLTeamFromClientStorage.id;
        }
      }

      const defaultNFLTeam = defaultNFLTeamId
        ? nflTeams.find((team:Team) => team.id === defaultNFLTeamId)
        : nflTeams[0];

      setSelectedNFLTeam(defaultNFLTeam);
    }
  }, [
    user,
    nflTeams?.length,
    selectedNFLTeam?.id,
    printed,
    selectedNFLTeamIdFromQueryParameters,
  ]);

  useEffect(() => {
    if (player && selectedCollegeTeam?.id) {
      fetchTeam(selectedCollegeTeam.id)
        .then((team:void | Team) => {
          if (team) {
            setSelectedCollegeTeam(team);
          }
        });
      fetchOn3NilValuation(player.id);
    }
  }, [player, selectedCollegeTeam?.id]);

  useEffect(() => {
    if (player && selectedPosition && selectedCollegeTeam?.id) {
      fetchOn3NilValuation(player.id);
    }
  },[selectedPosition, selectedCollegeTeam?.id])

  useEffect(() => {
    if (player && selectedNFLTeam?.id && nflAccess) {
      fetchTeam(selectedNFLTeam.id)
        .then((team:void | Team) => {
          if (team) {
            setSelectedNFLTeam(team);
          }
        });
    }
  }, [player, selectedNFLTeam?.id, nflAccess]);

  useEffect(() => {
    if (player) {
      const primaryHS:HighSchool = (player.highSchools || []).find((highSchool:HighSchool) => highSchool && highSchool.isPrimary) || {} as HighSchool;
      const hsState = primaryHS && primaryHS.state ? primaryHS.state : '';
      const title = `${player.firstName} ${player.lastName}${hsState ? ` from ${hsState}` : ''}`;

      updatePageTitle(title);
    }
  }, [player]);

  useEffect(() => {
    if (player && !printed) {
      const playerHasColleges = player && player.playerColleges && player.playerColleges.length > 0;
      const playerHasNFLTeams = player && player.playerNFLTeams && player.playerNFLTeams.length > 0;

      let selectedTeamType = TEAM_TYPE.HIGH_SCHOOL;
      if (playerHasColleges) {
        selectedTeamType = TEAM_TYPE.COLLEGE;
      }

      if (playerHasNFLTeams) {
        selectedTeamType = TEAM_TYPE.NFL;
      }

      setSelectedTeamType(selectedTeamType);
    } else if (player && printed && selectedTeamTypeFromQueryParameters) {
      let selectedTeamType = TEAM_TYPE.HIGH_SCHOOL;

      if (selectedTeamTypeFromQueryParameters === 'college') {
        selectedTeamType = TEAM_TYPE.COLLEGE;
      } else if (selectedTeamTypeFromQueryParameters === 'nfl') {
        selectedTeamType = TEAM_TYPE.NFL;
      }

      setSelectedTeamType(selectedTeamType);
    } else if (player && printed && !selectedTeamTypeFromQueryParameters) {
      setSelectedTeamType(TEAM_TYPE.HIGH_SCHOOL);
    }
  }, [player, nflAccess, printed, selectedTeamTypeFromQueryParameters]);

  function fetchPlayer(slug:string) {
    if (!loading) setLoading(true);

    gql(`
      mutation {
        viewPlayer (slug: "${slug}") {
          id
          slug
          firstName
          lastName
          cityStateZip
          photoUrl
          dob
          gpa
          sat
          act
          compStar
          _247Star
          _247Comp
          industryNationalRank
          committedDate
          pai
          combine
          ppi
          ppiDate
          nflCombineScore
          highSchools
          multiSport
          linkMilesplit
          linkMaxpreps
          link247
          linkRivals
          linkAthleticNet
          linkPff
          linkOn3
          linkSSA
          linkInstagram
          linkDirectAthletics
          linkTwitter
          linkHudl
          linkTrackingFootball
          offers
          about
          isSpeedRecruit
          isPowerRecruit
          aTierCalculated
          bTierCalculated
          combineSpeedTier
          combinePowerTier
          combineAgilityTier
          trackFieldRatings
          combineRatings
          proCombineRatings
          allAmerican
          allConference
          allPro
          proBowl
          watchList
          committedToTeamId
          projectedPAI
          committedToTeam {
            id
            name
            slug
            logo247
            logoESPN
            logoAlt
            city
            state
            conference {
              mainLevel
            }
          }
          paiHasFreshmanData
          combineHasFreshmanData
          isArchived
          lastPublishedDate
          createdAt
          updatedAt
          ourladsId
          ryzerUrl
          miscUrl
          discovery
          cellPhone
          email
          address
          cityStateZip
          parentNames
          parentContact
          headCoach
          headCoachContact
          igaScore
          hsFilmUrl 
          depthChart
          ourladsUpdatedDate
          ryzerId
          pffUrl
          transferStatus
          transferYear
          on3PublicNILValuation
          on3Date
          prepRedzoneStateRank
          prepRedzoneStatePosRank
          prepRedzoneRiser
          industryStateRank
          trainerInfo
          industryPositionRank
          twitterFollowers
          instagramFollowers
          tikTokFollowers
          showEvaluationOnHeader
          playerAgencies {
            id
            agentName
            agency
            agentContact
          }
          playerCollegeMPHs {
            playerId
            mph
            mphPercentile
            distanceTraveled
            filmUrl
            year
            averageTop5MPH
            ncaaMPHRank
            ncaaMPHPercentile
            mphPositionRank
            mphPosition
            timesExceeded20
          }
          playerNILValues {
            id
            on3Date
            on3PublicNILValuation
          }
          bestHighSchoolMPH {
            id
            playerId
            mph
            mphPercentage
            timeToMax
            distanceTraveled
            type
            isBest
            confidencePercent
            filmUrl
            year
          }
          bestCollegeMPH {
            id
            playerId
            mph
            mphPercentile
            distanceTraveled
            filmUrl
            year
            averageTop5MPH
            ncaaMPHRank
            ncaaMPHPercentile
            mphPositionRank
            mphPosition
            timesExceeded20
          }
          playerColleges {
            id
            isPrimary
            isFormer
            recruitingClass
            positions
            height
            weight
            jerseyNumber
            url
            createdAt
            team {
              id
              name
              slug
              logo247
              logoESPN
              logoAlt
              city
              state
              conference {
                mainLevel
              }
            }
          }
          playerNFLTeams {
            teamId
            isPrimary
            draftYear
            positions
            draftRound
            draftPick
            height
            weight
            heightPerc
            weightPerc
            team {
              name
              slug
              logo247
              logoESPN
              logoAlt
              city
              state
            }
          }
          hsCombines {
            id
            combineDate
            type
            wingspan
            wingspanPerc
            arm
            armPerc
            hand
            handPerc
            height
            heightPerc
            weight
            weightPerc
            teamCamp
            updatedAt
          }
          nflCombines {
            id
            combineDate
            height
            weight
            wingspan
            arm
            hand
            heightPerc
            weightPerc
            armPerc
            handPerc
            wingspanPerc
          }
          notes {
            text
            colorCode
          }
        }
      }
    `)
      .then((data:any) => ({
        player: data.viewPlayer as Player,
        errorMessage: data.errorMessage,
      }))
      .then(({ player, errorMessage }:{ player:Player, errorMessage:string }) => {
        if (player) {
          fetchOn3NilValuation(player.id);
          setPlayer(player);
        } else if (errorMessage === '403 Forbidden') {
          history.push(`/dashboard?no-access=${history.location.pathname}`);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchTeam (teamId:number) {
    if (!loading) setLoading(true);
    const cachedTeams = [
      retrieve(CacheKey.DIV_I_TEAMS),
      retrieve(CacheKey.NFL_TEAMS),
    ].filter(Boolean).flat();

    if (cachedTeams.length) {
      const team = cachedTeams.find((team:Team) => team.id === teamId);
      if (team) {
        setLoading(false);
        return Promise.resolve(team);
      }
    }

    return gql(`
      team (id: ${teamId}) {
        id
        type
        slug
        name
        logo247
        logoESPN
        logoAlt
        averagePAI
        pai
        combine
        proCombine
        ppi
        conference {
          iconUrl
        }
      }
    `)
      .then((data:any) => data.team as Team)
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  function updatePositions (player:Player) {
    const primarySchool = (player.highSchools || [])
      .find((highSchool:HighSchool) => highSchool && highSchool.isPrimary);
    const schoolPositions = (primarySchool || {}).positions || [];

    let positionsForEducation = schoolPositions;
    if (selectedTeamType === TEAM_TYPE.COLLEGE) {
      const primaryCollege = (player.playerColleges || [])
        .find((college:College) => college && college.isPrimary);
      const collegePositions = (primaryCollege || {}).positions || [];

      positionsForEducation = collegePositions;
    } else if (selectedTeamType === TEAM_TYPE.NFL) {
      const primaryNFLTeam = (player.playerNFLTeams || [])
        .find((nflTeam:NFLTeam) => nflTeam && nflTeam.isPrimary);
      const nflPositions = (primaryNFLTeam || {}).positions || [];

      positionsForEducation = nflPositions;
    }

    const positions:Position[] = positionsForEducation
      .filter((position:any) => position?.code)
      .sort((first:any, second:any) => {
        if (first.isPrimary) return -1;
        if (second.isPrimary) return 1;
        return 0;
      })
      .map((position:any) => position.code);

    setPositions(positions);
    setSelectedPosition(selectedPositionFromQueryParameters as Position || positions[0]);
  }

  function onEditPlayer () {
    if (!player || !player.slug) return;

    setLoading(true);

    gql(`
      mutation {
        editPlayer(slug: "${player.slug}")
      }
    `)
      .then((data:any) => data.editPlayer as boolean)
      .then((success:boolean) => {
        if (success) {
          history.push(`/import#scrollto-${player.slug}`);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function showToast(message:any, type:ToastType = ToastType.SUCCESS) {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }

  function fetchOn3NilValuation(playerId:number) {
    if (!selectedPosition || !selectedCollegeTeam?.id) return;
    gql(`
        getOn3Valuations(playerId: ${playerId}, positionCode: "${selectedPosition}", teamId: ${selectedCollegeTeam?.id}) {
          positionCode
          playerId
          teamId
          on3RosterValue {
            value
            direction
            allPercentile
            allRank
            allRankTotal
            positionPercentile
            positionRank
            positionRankTotal
            teamAllAverageValue
            teamAllRank
            teamAllRankTotal
            teamPositionAverageValue
            teamPositionRank
            teamPositionRankTotal
          }
          on3PrivateNILValue {
            value
            direction
            allPercentile
            allRank
            allRankTotal
            positionPercentile
            positionRank
            positionRankTotal
            teamAllAverageValue
            teamAllRank
            teamAllRankTotal
            teamPositionAverageValue
            teamPositionRank
            teamPositionRankTotal
          }
          on3PublicNILValuation {
            value
            direction
            allPercentile
            allRank
            allRankTotal
            positionPercentile
            positionRank
            positionRankTotal
            teamAllAverageValue
            teamAllRank
            teamAllRankTotal
            teamPositionAverageValue
            teamPositionRank
            teamPositionRankTotal
          }
      }
    `)
      .then((data:any) => data.getOn3Valuations)
      .then((valuations:any) => {
        if (valuations?.positionCode == selectedPosition) {
          setNILValuation(valuations);
        }
      })
      .catch(console.error);
  }

  const { data: hsProductionData } = useQuery<HighSchoolProductionResponse | null>(
    ['playerHsData', player?.id],
    () => gql(`
      getPlayerHsData (playerId: ${player?.id || 0}) {
        idType
        idValue
        hsProductionData {
          year
          hsName
          gamesPlayed
          passingComp
          passingAtt
          passingYds
          passingTD
          passingInt
          rushingAtt
          rushingYds
          rushingTD
          receiving
          receivingYds
          receivingTD
          tackles
          sacks
          interceptions
        }
      }
    `)
      .then((data:any) => data.getPlayerHsData as HighSchoolProductionResponse),
    {
      enabled: !!player?.id && selectedTeamType === TEAM_TYPE.HIGH_SCHOOL,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    },
  );

  const { data: evaluationTeam, isLoading: isEvaluationLoading } = useQuery<Team | null>(
      ['evaluationTeam', user?.program?.programTeamId],
      () => {
        const cachedDivITeams = retrieve(CacheKey.DIV_I_TEAMS);
        const cachedTeam = cachedDivITeams?.find((team:Team) => team.id === user?.program?.programTeamId);

        if (cachedTeam) {
          return Promise.resolve(cachedTeam);
        }

        return gql(`
              team (id: ${user?.program?.programTeamId}) {
                name
                logo247
                logoESPN
                logoAlt
              }
            `)
          .then((data: any) => data.team as Team);
      },
      {
        enabled: !!user?.program?.programTeamId,
        staleTime: 60000,
        refetchOnWindowFocus: false,
      },
    );

  const { data: playerEvaluationData, isLoading } = useQuery<PlayerEvaluationDataResponse | null>(
    ['playerEvaluationData', player?.id],
    () => gql(`
      getPlayerEvaluationData (playerId: ${player?.id || 0}) {
        accountName
        playerCode
        customFields
      }
    `)
      .then((data:any) => data.getPlayerEvaluationData as PlayerEvaluationDataResponse),
    {
      enabled: !!player?.id,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    },
  );

  // get player common data
  const { data: playerCommonData } = useQuery<PlayerCommonData | null>(
    ['getPlayerAllData', player?.id],
    () => gql(`
      getPlayerAllData (playerId: ${player?.id || 0}) {
        idType
        idValue
        offerQualityScore
        p4OfferCount
        g6OfferCount
        fcsOfferCount
        playerSeasonGrade {
          year
          week
          offense
          defense
        }
        playerOffers {
          teamName
          logo247
          logoAlt
          logoESPNDark
          logoESPN
        }
        awards {
          year
          awardId
          awardName
          isWatchlist
        }
        playerTransferPortal {
          transferStatus
          transferYear
          initiatedDate
          graduateStudent
          receivingAid
          athleticAid
          ncaaId
          designatedSaSports
          division
        }
        playerYearlyCollegeRoster {
          year
          position
          class
          teamName
          logo247
          logoAlt
          logoESPNDark
          logoESPN
        }
        valuations {
          id
          name
          description
          playerValuation
          playerValuationScore
          playerEligibleForValuation
        }
        playerFactors {
          shortName
          description
          playerFactorValue
        }
      }
    `)
      .then((data:any) => data.getPlayerAllData as PlayerCommonData),
    {
      enabled: !!player?.id,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    },
  );

  const { data: getPlayerCollegeData, isLoading: isGetPlayerCollegeDataLoading } = useQuery<PlayerCollegeDataResponse | null>(
    ['getPlayerCollegeData', player?.id],
    () => gql(`
      getPlayerCollegeData (playerId: ${player?.id || 0}) {
      idType
      idValue
      collegeProductionData {
        pffId  
        year
        teamIdType
        teamIdValue
        teamName
        conferenceMainLevel
        gamesPlayed
        gamesStarted
        passingComp
        passingAtt
        passingCompletionPercent
        passingYds
        passingRelPassYds
        passingTD
        passingInt
        passingYPA
        passingYPG
        passingRZComp
        passingRZAtt
        passingRZTD
        passingRZInt
        passingIntPercent
        passingEffPercent
        passingFD
        rushingAtt
        rushingYds
        rushingYPC
        rushingYPG
        rushingRelRushYds
        rushingRelYPC
        rushingTD
        rushingEffRuns
        rushing5PlusRuns
        rushing10PlusRuns
        rushing15PlusRuns
        rushing20PlusRuns
        rushingFD
        receiving
        receivingTar
        receivingYds
        receivingYPC
        receivingTD
        receivingYPG
        receivingRZRec
        receivingRZTar
        receiving3rdDnRec
        receiving3rdDnTar
        receiving10PlusPercent
        receiving15PlusPercent
        receiving25PlusPercent
        receiving40PlusPercent
        receivingFD
        receivingDrop
        defenseSoloTackles
        defenseAssistedTackles
        defenseTFL
        defenseTFLYds
        defenseSack
        defenseSackYds
        defenseQBHurry
        defensePassBreak
        defenseInt
        defenseIntRetYds
        defenseIntRetTD
        defenseFR
        defenseFRYds
        defenseFRTD
        defenseFF
        defenseSafety
        defenseBlockKick
        snaps
        punt
        puntYard
        kickoff
        kickoffTouchback
        kickoffOutOfBounds
        logo247
        logoAlt
        logoESPNDark
        logoESPN
      }
      jucoProductionData {
        year
        teamIdType
        teamIdValue
        teamName
        gamesPlayed
        gamesStarted
        passingComp
        passingAtt
        passingCompletionPercent
        passingYds
        passingYPA
        passingTD
        passingInt
        rushingAtt
        rushingYds
        rushingYPC
        rushingTD
        receiving
        receivingYds
        receivingYPC
        receivingTD
        defenseSoloTackles
        defenseAssistedTackles
        defenseTotalTackles
        defenseSack
        defenseSackYds
        defenseTFL
        defenseTFLYds
        defensePassBreak
        defenseInt
        defenseFF
        kickingFGM
        kickingFGA
        kickingXP
        kickingXPA
        punts
        puntYds
        puntsAverage
        kickReturns
        kickReturnYds
        kickReturnAverage
        kickReturnTD
        puntRet
        puntRetYds
        puntRetAverage
        puntRetTD
      }
      naiaProductionData {
        year
        teamIdType
        teamIdValue
        teamName
        gamesPlayed
        gamesStarted
        passingComp
        passingAtt
        passingCompletionPercent
        passingYds
        passingYPA
        passingTD
        passingInt
        rushingAtt
        rushingYds
        rushingYPC
        rushingTD
        receiving
        receivingYds
        receivingYPC
        receivingTD
        defenseSoloTackles
        defenseAssistedTackles
        defenseTotalTackles
        defenseSack
        defenseSackYds
        defenseTFL
        defenseTFLYds
        defensePassBreak
        defenseInt
        defenseFF
        kickingFGM
        kickingFGA
        kickingXP
        kickingXPA
        punts
        puntYds
        puntsAverage
        kickReturns
        kickReturnYds
        kickReturnAverage
        kickReturnTD
        puntRet
        puntRetYds
        puntRetAverage
        puntRetTD
      }
      specialTeamsParticipations {
        year
        specialTeamsType
        playCount
      }
      playerUsageData {
        year
        side
        totalPlays
        slotOutsideTotal
        slotTotal
        outsideTotal
        leftRightSideTotal
        leftSideTotal
        rightSideTotal
        rushCoverageTotal
        rushedTotal
        coverageTotal
        leftTackleTotal
        leftGuardTotal
        centerTotal
        rightGuardTotal
        rightTackleTotal
        tackleGuardCenterTotal
        boxPlayerTotal
        alignments {
          name
          plays
          slot
          outside
          leftSide
          rightSide
          leftTackle
          leftGuard
          center
          rightGuard
          rightTackle
          boxPlayer
          rushed
          coverage
        }
      }
      playerGameLog {
        gameId
        teamId
        opponentTeamId
        hasStarted
        offensiveGradesOverall
        offensiveGradesPass
        offensiveGradesRun
        offensiveGradesRec
        offensiveGradesRunBlock
        offensiveGradesPassBlock
        defensiveGradesOverall
        defensiveGradesPassRush
        defensiveGradesRunDef
        defensiveGradesCover
        snapsTotal
        snapsRun
        snapsPass
        snapsRec
        snapsRunBlock
        snapsPassBlock
        snapsPassRush
        snapsRunDef
        snapsCover
        createdAt
        opponentTeam {
          name
        }
        teamScore
        opponentTeamScore
        gameDate
        isHomeTeam
      }
      playerSSAGameLog {
        gameId
        playerId
        teamId
        opponentTeamId
        hasStarted
        passComp
        passAtt
        passYard
        passTD
        passInt
        rushAtt
        rushYard
        rushTD
        rec
        recYards
        recTD
        tackleSolo
        tackleAssist
        sack
        tackleForLoss
        intRet
        fumbleForced
        fumRet
        createdAt
        updatedAt
        opponentTeam {
            name
          }
        teamScore
        opponentTeamScore
        gameDate
        isHomeTeam
      }
      playerYearlyFieldGoalStatistics {
        year
        fieldGoalAttempts
        fieldGoalMade
        expectedFieldGoalPercent
        fieldGoalLong
        xpAttempts
        xpMade
        fg10to19Made
        fg10to19Attempts
        fg20to29Made
        fg20to29Attempts
        fg30to39Made
        fg30to39Attempts
        fg40to49Made
        fg40to49Attempts
        fg50to59Made
        fg50to59Attempts
        fg60PlusMade
        fg60PlusAttempts
      }
      }
    `)
      .then((data:any) => data.getPlayerCollegeData as PlayerCollegeDataResponse),
    {
      enabled: !!player?.id && (selectedTeamType === TEAM_TYPE.COLLEGE || selectedTeamType === TEAM_TYPE.NFL),
      staleTime: 60000,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (getPlayerCollegeData) {
      setCollegeProductionData(getPlayerCollegeData);
    }
  }, [getPlayerCollegeData]);

  const playerOffers = useMemo<PlayerOffersResponse | null>(() => {
    if (!playerCommonData) {
      return null;
    }

    return {
      idType: playerCommonData.idType,
      idValue: playerCommonData.idValue as unknown as number,
      offerQualityScore: playerCommonData.offerQualityScore,
      p4OfferCount: playerCommonData.p4OfferCount,
      g6OfferCount: playerCommonData.g6OfferCount,
      fcsOfferCount: playerCommonData.fcsOfferCount,
      playerOffers: playerCommonData.playerOffers,
    };
  }, [playerCommonData]);

  const playerCareerData = useMemo<PlayerCareerDataResponse | null>(() => {
    if (!playerCommonData) {
      return null;
    }

    return {
      idType: playerCommonData.idType,
      idValue: playerCommonData.idValue,
      awards: playerCommonData.awards,
      playerTransferPortal: playerCommonData.playerTransferPortal,
      playerYearlyCollegeRoster: playerCommonData.playerYearlyCollegeRoster,
    };
  }, [playerCommonData]);

  const playerValuationFactors = useMemo<PlayerValuationProfileFactors | null>(() => {
    if (!playerCommonData) {
      return null;
    }

    return {
      valuations: playerCommonData.valuations,
      playerFactors: playerCommonData.playerFactors,
    };
  }, [playerCommonData]);

  useEffect(() => {
    if (playerCommonData && player) {
      const tfvsData = playerCommonData.valuations?.find((valuation) => valuation.name === 'TF College Valuation');
      const tfvs = tfvsData?.playerValuationScore;
      const tfvsValuation = tfvsData?.playerValuation;
      setPlayerCopy({ ...player, tfvs, tfvsValuation });
    }
  }, [playerCommonData, player]);

  const playerHasHighSchools = player && player.highSchools && player.highSchools.length > 0;
  const playerHasColleges = player && player.playerColleges && player.playerColleges.length > 0;
  const playerHasNFLTeams = player && player.playerNFLTeams && player.playerNFLTeams.length > 0;

  const primaryHighSchool = getPrimaryHighSchool(player);

  const trackFieldRating = (!!player?.trackFieldRatings?.length && selectedPosition)
    ? player.trackFieldRatings.find(rating => rating.positionCode === selectedPosition)
    : undefined;
  const combineRatings = (!!player?.combineRatings?.length && selectedPosition)
    ? player.combineRatings.filter(rating => rating.positionCode === selectedPosition)
    : undefined;
  const proCombineRatings = (!!player?.proCombineRatings?.length && selectedPosition)
    ? player.proCombineRatings.filter(rating => rating.positionCode === selectedPosition)
    : undefined;
  const hsInGameRating = {
    MPH: player && player?.bestHighSchoolMPH?.mph || 0,
    MPHPercentage: player && player?.bestHighSchoolMPH?.mphPercentage || 0,
    timeToMax: player && player?.bestHighSchoolMPH?.timeToMax || 0,
    distanceTraveled:player && player?.bestHighSchoolMPH?.distanceTraveled || 0,
    mphConfidencePercent: player && player?.bestHighSchoolMPH?.confidencePercent || 0,
    year: player && player?.bestHighSchoolMPH?.year || 0,
    filmUrl: player && player?.bestHighSchoolMPH?.filmUrl || '',
   }
   const collegeInGameRating = {
    MPH: player && player?.bestCollegeMPH?.mph || 0,
    MPHPercentage: player && player?.bestCollegeMPH?.mphPercentile || 0,
   }

  const collegeInGameStats = player?.playerCollegeMPHs || [];
  const linkPff = player?.linkPff
  

  const isContactInfoAvailable = player && (player?.email || player?.cellPhone || player?.address)
  const playerNilValues = player?.playerNILValues || [];
  const playerOn3Date = player?.on3Date || '';
  const IS_ADMIN_OR_NFL_SCOUT_OR_COLLEGE: boolean = [AccessLevelType.ADMIN, AccessLevelType.NFL, AccessLevelType.COLLEGE].includes(user?.accessLevel?.type as AccessLevelType);//user?.accessLevel?.type === AccessLevelType.ADMIN || user?.accessLevel?.type === AccessLevelType.NFL || user?.accessLevel?.type === AccessLevelType.COLLEGE;
  const isProductionDataAvailable = (selectedTeamType === TEAM_TYPE.HIGH_SCHOOL && !!hsProductionData?.hsProductionData?.length) || ((selectedTeamType === TEAM_TYPE.COLLEGE || selectedTeamType === TEAM_TYPE.NFL) && (!!collegeProductionData?.collegeProductionData?.length || !!collegeProductionData?.jucoProductionData?.length || !!collegeProductionData?.naiaProductionData?.length));
  const isOn3NILValuationAvailable = !!nilValuation?.on3PublicNILValuation?.value || !!playerValuationFactors?.playerFactors?.length;
  const hsInGameAvailable = !!hsInGameRating?.MPH;

  const sortedHSProductionData = useMemo(
    () => hsProductionData
      ? {
        ...hsProductionData,
        hsProductionData: sortRowsByKey(hsProductionData.hsProductionData, 'year', 'desc'),
      }
      : null,
    [hsProductionData],
  );

  const sortedCollegeProductionData = useMemo(
    () => sortRowsByKey(collegeProductionData?.collegeProductionData, 'year', 'desc'),
    [collegeProductionData?.collegeProductionData],
  );

  const sortedSpecialTeamsProductionData = useMemo(() => {
    const fieldGoalStatisticsByYear = new Map(
      (collegeProductionData?.playerYearlyFieldGoalStatistics || [])
        .map(statistics => [statistics.year, statistics]),
    );

    return sortedCollegeProductionData.map(row => ({
      ...row,
      ...fieldGoalStatisticsByYear.get(row.year),
    }));
  }, [collegeProductionData?.playerYearlyFieldGoalStatistics, sortedCollegeProductionData]);

  const sortedJucoProductionData = useMemo(
    () => sortRowsByKey(collegeProductionData?.jucoProductionData, 'year', 'desc'),
    [collegeProductionData?.jucoProductionData],
  );

  const sortedNaiaProductionData = useMemo(
    () => sortRowsByKey(collegeProductionData?.naiaProductionData, 'year', 'desc'),
    [collegeProductionData?.naiaProductionData],
  );

  const sortedSpecialTeamsParticipations = useMemo(
    () => sortRowsByKey(collegeProductionData?.specialTeamsParticipations, 'year', 'desc'),
    [collegeProductionData?.specialTeamsParticipations],
  );

  return (
    <>
    {!printed && (
      <PageContainer
        className={clsx(
          classes.playerPageTemplate,
          printed && classes.printed,
        )}
      >
        <Paper className={classes.paper}>
            <UpdatedPlayerProfileHeader
              loading={loading || divITeamsLoading || nflTeamsLoading}
              user={user}
              player={playerCopy || player}
              divITeams={divITeams}
              nflTeams={nflTeams}
              selectedTeamType={selectedTeamType}
              setSelectedTeamType={setSelectedTeamType}
              selectedCollegeTeam={selectedCollegeTeam}
              selectedNFLTeam={selectedNFLTeam}
              selectedPosition={selectedPosition}
              onCollegeTeamSelected={(collegeTeam:Team | null) => {
                if (collegeTeam) {
                  setSelectedCollegeTeam(collegeTeam);
                }
              }}
              onNFLTeamSelected={(nflTeam:Team | null) => {
                if (nflTeam) {
                  setSelectedNFLTeam(nflTeam);
                }
              }}
              setLoading={setLoading}
              showToast={showToast}
              onEditPlayer={onEditPlayer}
              isNFL={nflAccess}
              isAdmin={adminAccess}
              IS_ADMIN_OR_NFL_SCOUT={IS_ADMIN_OR_NFL_SCOUT_OR_COLLEGE}
              positions={positions}
              setSelectedPosition={setSelectedPosition}
              evaluationTeam={evaluationTeam}
              playerEvaluationData={playerEvaluationData}
              playerSeasonGrade={playerCommonData?.playerSeasonGrade}
              onScoreNavigate={onHeaderScoreNavigate}
            />
          <div ref={stickyHeaderRef} className={classes.stickyHeader}>
            <div >
              <PlayerProfileStickyHeader 
                player={player}
                selectedTeamType={selectedTeamType}
                setSelectedTeamType={setSelectedTeamType}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                isProductionDataAvailable={isProductionDataAvailable}
                isOn3NILValuationAvailable={isOn3NILValuationAvailable}
                hsInGameAvailable={hsInGameAvailable}
              />
            </div>
          </div>
            <div className={classes.detailsWrap} id='overview'>
              {
                selectedTeamType === TEAM_TYPE.HIGH_SCHOOL
                && (
                  <UpdatedPlayerProfileHighSchoolOverview 
                    player={playerCopy || player}
                    selectedPosition={selectedPosition}
                    playerNilValues={playerNilValues}
                    playerOn3Date={playerOn3Date}
                    playerOffers={playerOffers}
                  />
                )
              }
              {
                selectedTeamType === TEAM_TYPE.COLLEGE
                && 
                  <UpdatedPlayerProfileCollegeOverview 
                    player={playerCopy || player}
                    selectedPosition={selectedPosition}
                    user={user}
                    playerOn3Date={playerOn3Date}
                    collegeProductionData={sortedCollegeProductionData}
                    playerOffers={playerOffers}
                    starterYear={starterYear}
                  />
              }
              {
                selectedTeamType === TEAM_TYPE.NFL
                &&
                  <UpdatedPlayerProfileNFLOverview 
                    player={player}
                    selectedPosition={selectedPosition}
                    user={user}
                    playerOffers={playerOffers}
                  />
              }
              {!!collegeProductionData?.collegeProductionData?.length && (selectedTeamType === TEAM_TYPE.NFL || selectedTeamType === TEAM_TYPE.COLLEGE) &&
                  <CollegeCareerOverview
                    player={player}
                    collegeProductionData={sortedCollegeProductionData}
                    playerSSAGameLogData={getPlayerCollegeData?.playerSSAGameLog}
                    isPlayerSSAGameLogLoading={isGetPlayerCollegeDataLoading}
                  />
              }
              {/* high school production data table */}
              {selectedTeamType === TEAM_TYPE.HIGH_SCHOOL && !!hsProductionData?.hsProductionData?.length && 
                <div id='production'>
                  <HighSchoolProductionTable className={classes.box} hsProductionData={sortedHSProductionData}/>
                </div>
              }
              {/* special Teams Data */}
              {
                selectedTeamType === TEAM_TYPE.COLLEGE && selectedPosition && SPECIAL_POSITIONS.includes(selectedPosition) 
                ?
                <>
                  {selectedPosition && SPECIAL_POSITIONS.includes(selectedPosition) && (
                    <>
                      <div id='specialTeamYearlyPunt'>
                        <SpecialTeamYearlyPuntKickoffStatsTable data={sortedSpecialTeamsProductionData} />
                      </div>
                      {!!collegeProductionData?.specialTeamsParticipations?.length &&
                      <div id='specialTeamsPartiicipation'>
                        <SpecialTeamsPlayerParticipationTable data={sortedSpecialTeamsParticipations}/>
                      </div>
                      }
                    </>
                  )}
                </>
                :
                <>
                  {/* Juco production data table */}
                  {selectedTeamType === TEAM_TYPE.COLLEGE && !!collegeProductionData?.jucoProductionData?.length &&
                  <div id='production'>
                    <JucoProductionTable label="JUCO" className={classes.box} data={sortedJucoProductionData}/>
                  </div>
                  }
                  {/* NAIA production data table */}
                  {selectedTeamType === TEAM_TYPE.COLLEGE && !!collegeProductionData?.naiaProductionData?.length &&
                  <div id='production'>
                    {/* <NAIAProductionTable className={classes.box} data={sortedNaiaProductionData}/> */}
                      <JucoProductionTable label="NAIA" className={classes.box} data={sortedNaiaProductionData}/>
                  </div>
                  }
                  {/* College production data */}
                  {!!collegeProductionData?.collegeProductionData?.length && (selectedTeamType === TEAM_TYPE.NFL || selectedTeamType === TEAM_TYPE.COLLEGE) &&
                  <div id='production'>
                    <CollegeNFLProductionTable
                      starterYear={starterYear}
                      playerCareerData={playerCareerData}
                      playerUsageData={collegeProductionData?.playerUsageData}
                      playerPFFGameLogData={getPlayerCollegeData?.playerGameLog}
                      isPlayerPFFGameLogLoading={isGetPlayerCollegeDataLoading}
                      player={player}
                      data={sortedCollegeProductionData}
                    />
                  </div>
                  }
                </>
              }


            <Grid
              className={classes.gridContainer}
              container
            >
              <Grid
                className={classes.gridContainerColumn}
                item
                xs={12}
                md={6}
              >

                {
                  selectedTeamType === TEAM_TYPE.NFL &&
                  <div id='proCombine' className={classes.box}>
                    <UpdatedPlayerProCombineRating
                      className={classes.box}
                      proCombineRatings={proCombineRatings}
                      nflCombines={player?.nflCombines || []}
                      player={player}
                      selectedPosition={selectedPosition}
                    />
                  </div>
                }
                {/* College MPH */}
                {
                  IS_ADMIN_OR_NFL_SCOUT_OR_COLLEGE 
                  && collegeInGameStats.length > 0 
                  && selectedTeamType === TEAM_TYPE.COLLEGE && (
                    <div id='collegeInGame' className={classes.box}>
                      <PFFPlayerInGameRating
                        className={classes.box}
                        collegeInGameStats={collegeInGameStats}
                        linkPffUrl={linkPff}
                      />
                    </div>
                  )
                }
                <HighSchoolBiometrics
                  className={classes.box}
                  player={player}
                  selectedPosition={selectedPosition}
                />

                <div id='hsRatings' className={classes.box}>
                  <PlayerTrackAndFieldRating
                    className={classes.box}
                    trackFieldRating={trackFieldRating}
                    player={player}
                    isNFL={nflAccess}
                  />
                  
                  <PlayerCombineRating
                    className={classes.box}
                    combineRatings={combineRatings}
                    player={player}
                    isNFL={nflAccess}
                  />
                </div>
                    {/* HS MPH */}
                <div id='hsInGame' className={classes.box}>
                  {IS_ADMIN_OR_NFL_SCOUT_OR_COLLEGE && hsInGameRating?.MPH && selectedTeamType === TEAM_TYPE.HIGH_SCHOOL 
                  ? <PlayerInGameRating
                    className={classes.box}
                    inGameRating={hsInGameRating}
                    label={"High School In-Game Rating"}
                  /> : ''}
                </div>
                  {/* {(user && user.accessLevel && user.accessLevel.hasEvalDotAndNotes && player?.notes) && (
                    <div id='notes' className={classes.notesContainer}>
                      <PlayerProfileNotes
                        className={classes.box}
                        notes={player.notes}
                        playerId={player.id}
                      />
                    </div>
                  )} */}
              </Grid>
              <Grid
                className={clsx(classes.gridContainerColumn)}
                item
                xs={12}
                md={6}
              >
                <UpdatedPlayerProfileStats
                  player={player}
                  collegeTeam={selectedCollegeTeam}
                  collegeTeamId={selectedCollegeTeam?.id}
                  nflTeam={selectedNFLTeam}
                  nflTeamId={selectedNFLTeam?.id}
                  position={selectedPosition}
                  isNFL={nflAccess}
                  selectedTeamType={selectedTeamType}
                  requestedTabValue={requestedProfileStatsTab?.value}
                  requestedTabKey={requestedProfileStatsTab?.key}
                />
              </Grid>
            </Grid>

            {
              nilValuation?.on3PublicNILValuation?.value && (
                <div id='valuation'>
                  <On3NilValuation
                    className={classes.box}
                    playerNilValues={nilValuation}
                    selectedTeam={selectedCollegeTeam}
                  />
                </div>
              )
            }
 
            {/* player valuations */} 
            {
              playerValuationFactors?.playerFactors?.length && evaluationTeam ? (
                <div id='valuation'>
                  <PlayerValuations evaluationTeam={evaluationTeam} data={playerValuationFactors} />
                </div>
              ) : null
            }

            <div id='comps'>
              {
                player && 
                  <PlayerProfileTopPlayers
                    className={classes.topPlayers}
                    currentPlayer={player}
                    user={user}
                    selectedTeamType={selectedTeamType}
                  />
              }
            </div>
          </div>
        </Paper>
      </PageContainer>
    )}
    </>
  )
}



const mapStateToProps = (state:State) => {
  const userState = state.user as UserState;

  return {
    user: state.user,
    nflAccess: state.ui.nflAccess,
    adminAccess: state.ui.adminAccess,
    divITeams: state.teams.divITeams,
    divITeamsLoading: state.teams.divITeamsLoading,
    nflTeams: state.teams.nflTeams,
    nflTeamsLoading: state.teams.nflTeamsLoading,
    selectedCollegeTeam: state.ui.selectedCollegeTeam,
    selectedNFLTeam: state.ui.selectedNFLTeam,
  };
};

const mapDispatchToProps =  (dispatch:Dispatch) => {
  return bindActionCreators(
    {
      fetchDivITeams,
      fetchNFLTeams,
      setNFLAccessAction,
      setSelectedCollegeTeam,
      setSelectedNFLTeam,
    },
    dispatch
  )
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UpdatedPlayerPageTemplate);
